import { useClientData } from '@/providers/ClientData.provider';
import useFetchClientBankAccounts from '@/hooks/useApi/useFetchClientBankAccounts';
import useFetchInteracEmail from '@/hooks/useApi/useFetchInteracEmail';
import { PaymentMethodType, NumbersOnly, ELinxConnectionMethod } from 'vopay';

import { PaymentFieldDefault } from './types';

export const getPaymentSelectionMethod = (paymentMethod?: PaymentMethodType): ELinxConnectionMethod | '' => {
  if (paymentMethod === 'Email' || paymentMethod === 'PhoneNumber') {
    return 'email';
  } else if (paymentMethod === 'BankAccount') {
    return 'bank';
  } else if (paymentMethod === 'CreditCard') {
    return 'credit';
  } else if (paymentMethod === 'DebitCard') {
    return 'debitcard';
  } else if (paymentMethod === 'PayPal') {
    return 'paypal';
  } else if (paymentMethod === 'Venmo') {
    return 'venmo';
  } else if (paymentMethod === 'Zelle') {
    return 'zelle';
  }
  return '';
};

export const mapToPaymentSelectionValue = (paymentMethod: PaymentMethodType): string => {
  const mapping: Record<PaymentMethodType, string> = {
    BankAccount: 'bank',
    CreditCard: 'credit',
    DebitCard: 'debitcard',
    PhoneNumber: 'email',
    Email: 'email',
    GooglePay: 'googlepay',
    PayPal: 'paypal',
    ApplePay: 'applepay',
    Venmo: 'venmo',
    Zelle: 'zelle',
  };

  return mapping[paymentMethod] || '';
};

export const useHandlePaymentMethodChange = () => {
  const [, , , fetchClientBankAccounts] = useFetchClientBankAccounts({});
  const [, , , fetchInteracEmail] = useFetchInteracEmail({});
  const {
    response: { clientAccountsEnabled },
  } = useClientData();

  const handlePaymentMethodChange = async (obj: {
    Token: string;
    PaymentMethod: PaymentMethodType;
    Contact: string;
    Value: string;
  }): Promise<{
    values: Partial<PaymentFieldDefault>;
    touched?: {
      [key in keyof PaymentFieldDefault]?: boolean;
    };
  } | null> => {
    const baseValues = { PaymentMethod: obj.Token, ProvideOrRequestPaymentInformation: 'provide' };
    if (obj.Value === 'new') {
      // Handle "new" payment method
      return {
        values: {
          ...baseValues,
          PaymentSelectionMethod: '',
          EtransferConnectionType: 'email',
          Answer: '',
          ClientReferenceNumber: '',
          Question: '',
          RecipientEmailAddress: '',
          RecipientPhoneNumber: '',
          FinancialInstitutionNumber: '',
          BranchTransitNumber: '',
          ABARoutingNumber: '',
          AccountNumber: '',
          Address1: '',
          City: '',
          Province: '',
          PostalCode: '',
          BankPhoneNumber: '',
        },
        touched: {
          Answer: false,
          ClientReferenceNumber: false,
          Question: false,
          RecipientEmailAddress: false,
          RecipientPhoneNumber: false,
          FinancialInstitutionNumber: false,
          BranchTransitNumber: false,
          ABARoutingNumber: false,
          AccountNumber: false,
          Address1: false,
          City: false,
          Province: false,
          Country: false,
          PostalCode: false,
          BankPhoneNumber: false,
        },
      };
    }

    const paymentSelectionMethod = getPaymentSelectionMethod(obj.PaymentMethod);
    try {
      // Handle Interac email/phone
      if (obj.PaymentMethod === 'Email' || obj.PaymentMethod === 'PhoneNumber') {
        const interacEmailResponse = await fetchInteracEmail({
          Token: obj.Token,
          ClientAccountID: clientAccountsEnabled ? obj.Contact : undefined,
        });
        if (!interacEmailResponse.Success) {
          throw new Error(interacEmailResponse.ErrorMessage);
        }

        const interacEmailDetails = Object.values(interacEmailResponse.InteracEmails).find(
          (email) => obj.Token === email.Token,
        );

        if (!interacEmailDetails) {
          return {
            values: {
              ...baseValues,
              PaymentSelectionMethod: paymentSelectionMethod,
            },
          };
        }

        return {
          values: {
            ...baseValues,
            EtransferConnectionType: obj.PaymentMethod === 'PhoneNumber' ? 'phone' : 'email',
            Answer: interacEmailDetails.Answer || '',
            ClientReferenceNumber: interacEmailDetails.ClientReferenceNumber || '',
            Question: interacEmailDetails.Question || '',
            RecipientEmailAddress: interacEmailDetails.RecipientEmailAddress || '',
            RecipientName: interacEmailDetails.RecipientName || '',
            RecipientPhoneNumber: interacEmailDetails.RecipientPhoneNumber || '',
            PaymentSelectionMethod: paymentSelectionMethod,
          },
          touched: {
            Answer: false,
            ClientReferenceNumber: false,
            Question: false,
            RecipientEmailAddress: false,
            RecipientName: false,
            RecipientPhoneNumber: false,
          },
        };
      }

      // Handle bank accounts
      if (paymentSelectionMethod === 'bank') {
        const bankAccountsResponse = await fetchClientBankAccounts({
          Token: obj.Token,
          ClientAccountID: clientAccountsEnabled ? obj.Contact : undefined,
        });
        if (!bankAccountsResponse.Success) {
          throw new Error(bankAccountsResponse.ErrorMessage);
        }
        const bankInfo = Object.values(bankAccountsResponse.BankAccounts).find((bank) => obj.Token === bank.Token);

        if (!bankInfo) {
          return {
            values: {
              ...baseValues,
              PaymentSelectionMethod: paymentSelectionMethod,
            },
          };
        }

        return {
          values: {
            ...baseValues,
            RecipientName: bankInfo.AccountHolderName,
            PaymentSelectionMethod: paymentSelectionMethod,
            RecipientPhoneNumber: NumbersOnly(bankInfo?.Phone || ''),
            BankPhoneNumber: NumbersOnly(bankInfo?.Phone || ''),
            FinancialInstitutionNumber: clientAccountsEnabled ? '' : bankInfo?.FinancialInstitutionNumber || '',
            BranchTransitNumber: clientAccountsEnabled ? '' : bankInfo?.BranchTransitNumber || '',
            ABARoutingNumber: clientAccountsEnabled ? '' : bankInfo?.ABARoutingNumber || '',
            AccountNumber: clientAccountsEnabled ? '' : bankInfo?.AccountNumber || '',
            RecipientEmailAddress: bankInfo?.EmailAddress || '',
            ClientReferenceNumber: bankInfo?.ClientReferenceNumber,
          },
        };
      }
    } catch (error) {
      console.error(error);
    }

    return {
      values: {
        ...baseValues,
        PaymentSelectionMethod: paymentSelectionMethod,
      },
    };
  };

  return handlePaymentMethodChange;
};
